import { Icon } from '@clay-run/glaze/icon';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import type { AccountHierarchyNode } from '@shared/flow/account-hierarchy';
import { FLOW_ICONS } from '../../icons';
import { SEPARATE_BUYING_CENTER_LABEL, buyingCenterText } from './hierarchyPresentation';

/**
 * A checked-checkbox glyph + "Separate buying center", with a tooltip holding the
 * account's summary sentence. Renders nothing for unflagged accounts (no empty checkbox).
 * `compact` drops the text and keeps the glyph (the table has its own column header).
 *
 * The trigger is a focusable span (not a button): in the graph it sits inside the card's own
 * button, and a button inside a button is invalid. The panel opens to the right so it does not
 * stack on the card's own metadata tooltip.
 */
export function SeparateBuyingCenterIndicator({ node, compact = false }: { node: AccountHierarchyNode; compact?: boolean }) {
  if (!node.separate_buying_center) return null;
  return (
    <Tooltip>
      <TooltipTrigger>
        <span
          tabIndex={0}
          aria-label={compact ? SEPARATE_BUYING_CENTER_LABEL : undefined}
          data-testid={`separate-buying-center-${node.id}`}
          className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-content-secondary"
        >
          <Icon type={FLOW_ICONS.separateBuyingCenter} weight="fill" size="xs" aria-hidden className="text-content-action" />
          {!compact && SEPARATE_BUYING_CENTER_LABEL}
        </span>
      </TooltipTrigger>
      <TooltipPanel className="max-w-xs whitespace-normal break-words" positioner={{ side: 'right' }}>
        {buyingCenterText(node)}
      </TooltipPanel>
    </Tooltip>
  );
}
